import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { AppShell } from "@/components/AppShell";
import { api, fmtKLDateTime } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const ACTIONS = [
  "login_success", "login_failed", "account_locked", "refresh_reuse_detected",
  "password_changed", "password_reset", "user_created", "user_updated",
  "user_deactivated", "user_deleted", "face_enrolled", "punch_approved",
  "punch_flagged", "punch_overridden", "review_queue_opened", "csv_exported",
  "photo_accessed", "initial_admin_seeded",
];

export default function AdminAuditPage() {
  const { t } = useLang();
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["audit", actor, action, dateFrom, dateTo, page],
    queryFn: async () => (await api.get("/admin/audit", {
      params: {
        actor, action: action === "all" ? "" : action,
        dateFrom, dateTo, page, pageSize: 50,
      },
    })).data,
  });

  const pages = Math.max(1, Math.ceil((data?.total || 0) / 50));

  return (
    <AppShell>
      <div className="space-y-4">
        <h1 className="text-xl font-semibold tracking-tight">{t("auditLog")}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder={t("actor")} value={actor} onChange={(e) => { setActor(e.target.value); setPage(1); }}
            data-testid="audit-actor-filter" className="h-9 max-w-xs" />
          <Select value={action} onValueChange={(v) => { setAction(v); setPage(1); }}>
            <SelectTrigger data-testid="audit-action-filter" className="h-9 w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("action")}: {t("allStatuses")}</SelectItem>
              {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            data-testid="audit-date-from" className="h-9 w-auto" />
          <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            data-testid="audit-date-to" className="h-9 w-auto" />
        </div>

        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table data-testid="audit-log-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("when")}</TableHead>
                <TableHead>{t("actor")}</TableHead>
                <TableHead>{t("action")}</TableHead>
                <TableHead>{t("target")}</TableHead>
                <TableHead>{t("detail")}</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">{t("loading")}</TableCell></TableRow>
              )}
              {!isLoading && (data?.items || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">{t("noResults")}</TableCell></TableRow>
              )}
              {data?.items?.map((e) => (
                <TableRow key={e.id} data-testid="audit-row">
                  <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">{fmtKLDateTime(e.timestamp)}</TableCell>
                  <TableCell className="text-xs">{e.actorEmail || "—"}</TableCell>
                  <TableCell className="text-xs font-medium">{e.action}</TableCell>
                  <TableCell className="text-xs">{e.targetType ? `${e.targetType}:${(e.targetId || "").slice(0, 8)}` : "—"}</TableCell>
                  <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground">
                    {e.detail && Object.keys(e.detail).length ? JSON.stringify(e.detail) : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.ip || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{data?.total ?? 0} rows</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev-page">←</Button>
            <span className="text-sm tabular-nums">{page}/{pages}</span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next-page">→</Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
